import { useState } from 'react';
import { apiClient, withErrorHandling, useAPIErrorHandling } from '../utils/apiErrorHandling';

/**
 * 제품 업데이트를 위한 훅
 */
export const useProductUpdate = (showSnackbarFn = null) => {
    const [saving, setSaving] = useState(false);
    const { handleAPIError, handleAPISuccess } = useAPIErrorHandling(showSnackbarFn);

    /**
     * 제품 정보 업데이트 (공통 함수)
     * @param {Object} product - 제품 정보
     * @param {Object} updates - 업데이트할 필드들
     * @param {string} successMessage - 성공 메시지
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateProductData = async (product, updates = {}, successMessage = '제품 정보가 저장되었습니다.') => {
        if (!product) {
            throw new Error('제품 정보가 없습니다.');
        }

        setSaving(true);

        try {
            // 기본 제품 데이터 구성
            const productData = {
                id: product.id,
                source_url: product.source_url,
                product_id: product.product_id,
                title: product.title,
                title_en: product.title_en,
                brand_name: product.brand_name,
                brand_name_en: product.brand_name_en,
                price: product.price,
                price_usd: product.price_usd,
                description: product.description,
                media_images: product.media_images || [],
                content_images: product.content_images || [],
                sale_status: product.sale_status,
                product_type: product.product_type || 'Merchandise',
                artist: product.artist || null,
                vendor: product.vendor || null,
                tags: product.tags || [],
                shopify_product_id: product.shopify_product_id,
                shopify_status: product.shopify_status,
                published_at: product.published_at,
                published_to_shopify: product.published_to_shopify,
                arrival_date: product.arrival_date,
                shipment_date: product.shipment_date,
                status: product.status, // 기본 status 값
                // 업데이트할 필드들로 덮어쓰기 (반드시 마지막에 위치)
                ...updates
            };

            // 위버스 사이트인 경우 artist 필드를 문자열로 유지
            if (product.source_site === 'weverse' && productData.artist && typeof productData.artist === 'object') {
                // 딕셔너리 형태인 경우 name 필드만 추출하여 문자열로 변환
                productData.artist = productData.artist.name || productData.artist;
            }

            console.log('=== 제품 업데이트 데이터 ===');
            console.log('업데이트할 필드:', updates);
            console.log('productData (업데이트 전):', { ...productData, ...updates });
            console.log('productData.status:', productData.status);
            console.log('successMessage:', successMessage);

            const result = await withErrorHandling(
                () => {
                    console.log('API 호출 시작: PUT /products');
                    console.log('요청 본문 status:', productData.status);
                    return apiClient.put('/products', productData);
                },
                {
                    onSuccess: () => {
                        console.log('API 호출 성공');
                        if (successMessage) {
                            handleAPISuccess(successMessage);
                        }
                    },
                    showSnackbar: !!successMessage,
                    showSnackbarFn: showSnackbarFn
                }
            );

            console.log('=== 업데이트 결과 ===');
            console.log('result:', result);
            console.log('result.success:', result?.success);
            console.log('result.data:', result?.data);

            return result;

        } catch (error) {
            console.error('제품 업데이트 중 오류 발생:', error);
            handleAPIError(error, '제품 정보 저장 중 오류가 발생했습니다.');
            return { success: false, error };
        } finally {
            setSaving(false);
        }
    };

    /**
     * 제품 정보 업데이트 (썸네일 포함)
     * @param {Object} product - 제품 정보
     * @param {Array} thumbnailImages - 썸네일 이미지 배열
     * @param {Set} selectedThumbnails - 선택된 썸네일 인덱스 Set
     * @param {string} selectedImage - 선택된 메인 이미지 URL
     * @param {string} vendor - 벤더 정보
     * @param {Array} tags - 태그 배열
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateProduct = async (product, thumbnailImages, selectedThumbnails, selectedImage, vendor = null, tags = null) => {
        // 선택된 썸네일들만 필터링
        const selectedImages = thumbnailImages.filter((_, index) => selectedThumbnails.has(index));
        
        // thumbnailImages가 비어있으면 기존 media_images 사용
        const finalMediaImages = thumbnailImages.length > 0 ? selectedImages : (product.media_images || []);
        
        const updates = {
            media_images: finalMediaImages,
            ...(vendor !== null && { vendor }),
            ...(tags !== null && { tags })
        };

        return updateProductData(product, updates, '제품 정보가 저장되었습니다.');
    };

    /**
     * 제품명 업데이트
     * @param {Object} product - 제품 정보
     * @param {string} titleEn - 영문 제품명
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateProductTitle = async (product, titleEn) => {
        const updates = { title_en: titleEn };
        return updateProductData(product, updates, '제품명(영문)이 저장되었습니다.');
    };

    /**
     * USD 가격 업데이트
     * @param {Object} product - 제품 정보
     * @param {number} priceUsd - USD 가격
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateProductPriceUsd = async (product, priceUsd) => {
        const updates = { price_usd: priceUsd };
        return updateProductData(product, updates, 'USD 가격이 저장되었습니다.');
    };

    /**
     * 상세 정보 업데이트
     * @param {Object} product - 제품 정보
     * @param {string} detailContent - 상세 내용
     * @param {Array} contentImages - 콘텐츠 이미지 배열
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateProductDetail = async (product, detailContent, contentImages) => {
        const updates = {
            description: detailContent,
            content_images: contentImages
        };
        return updateProductData(product, updates, '상세 정보가 저장되었습니다.');
    };

    return {
        saving,
        updateProductData,
        updateProduct,
        updateProductTitle,
        updateProductPriceUsd,
        updateProductDetail
    };
};
